// =============================================================================
// ui/tabs/ContractCheckIn.tsx — Session → Contract Check-In.
// -----------------------------------------------------------------------------
// Where the trader stands against the pledge they made that morning, and the one
// place they can say "the imported book is not the whole story — here is my real
// figure".
//
// ── ⚠ THE NUMBER IT JUDGES IS NOT NECESSARILY THE IMPORTED ONE ──
//
// Trades are imported after the close. Mid-session the book is empty, so a
// check-in that could only read imported trades would be useless at exactly the
// hour it exists for. Hence the override: type the live figure, press Log it, and
// every reading on this phase switches to it — the banner, and the glance tile
// above, which relabels itself "Logged P&L" so the two can never be confused.
//
// ── ⚠ CLEARING IS A FIRST-CLASS ACTION, AND `0` IS NOT CLEARING ──
//
// An empty box removes the override and the screen goes back to the imported
// figure. A typed `0` is a real self-reported flat day and must survive, which is
// why nothing here tests falsiness. `checkInToBlob` deletes the key rather than
// nulling it, for the same reason.
//
// ── ⚠ WHAT IT WILL NOT DO ──
//
// It does not stamp `dllHitAt` or `dllAcknowledgedAt`. Those are the record that
// a daily stop was breached and that the trader chose to keep going, and one of
// them is compared against every later trade to flag it as an override. Writing
// them from a check-in box would manufacture that history. The banner SAYS the
// stop is hit; acknowledging it stays with the old client until this one has a
// deliberate design for it. Stated on the tab, not implied by absence.
// =============================================================================

import { useState } from 'react';
import { useSupabase } from '../state/supabase';
import { saveCheckInPnl } from '../../io/sync/saveCheckInPnl';
import { checkInVerdict, hasCheckInContract } from '../../selectors/journal';
import type { SessionContract } from '../../selectors/journal';
import { money } from '../format';

export interface ContractCheckInProps {
  contract: SessionContract;
  date: string;
  /**
   * The imported figure for the day IN DOLLARS, or null while the trades load.
   *
   * 🚨 DOLLARS IS NOT A DETAIL HERE (#67). The verdict below compares this
   * against the contract's target and daily loss limit, which the trader wrote
   * in dollars — so handing it a points figure does not just mislabel a number,
   * it reaches the wrong verdict. On ES a −$1,000 day is −200 points, and −200
   * against a −$500 limit reads as **"On contract"** on the day it matters most.
   */
  livePnlUsd: number | null;
  /** The row's stored contract blob — merged over, never rebuilt. */
  rawContract: Record<string, unknown>;
  baselineUpdatedAt: string | null;
  onSaved: (
    contract: SessionContract, updatedAt: string | null,
    rawContract: Record<string, unknown>,
  ) => void;
}

export function ContractCheckIn(
  { contract, date, livePnlUsd, rawContract, baselineUpdatedAt, onSaved }: ContractCheckInProps,
) {
  const { sb, userId } = useSupabase();
  const override = contract.manualPnl != null && Number.isFinite(contract.manualPnl);

  // ⚠ SEEDED ONCE AND THAT IS CORRECT HERE, unlike the editors on this tab: the
  // whole component is remounted on a date change by its `key`, and a live
  // re-seed from `livePnlUsd` would overwrite what the trader is halfway through
  // typing every time a trade lands.
  const [draft, setDraft] = useState<string>(() => {
    if (override) return String(contract.manualPnl);
    return livePnlUsd == null ? '' : String(Math.round(livePnlUsd * 100) / 100);
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!hasCheckInContract(contract)) {
    // ⚠ BETA'S WORDING, VERBATIM. The port had paraphrased it ("a target and/or a
    // daily loss limit, accounts optional"), which reads the same and is not the
    // same: "DLL" is the term used on the contract form, in the glance tile above
    // and in beta's own warnings, so spelling it out here is the one place on the
    // screen where the abbreviation is not introduced.
    return (
      <p className="muted">
        Set a <b>Session Contract</b> in the Pre-Market view (a target and/or DLL —
        accounts optional) to track your standing against it here.
      </p>
    );
  }

  const write = async (manualPnl: number | null) => {
    setBusy(true);
    setError(null);
    const r = await saveCheckInPnl({
      sb, userId, date, manualPnl, rawContract, baselineUpdatedAt,
    });
    setBusy(false);
    if (r.error) { setError(r.error); return; }
    onSaved({ ...contract, manualPnl }, r.updatedAt, r.rawContract);
  };

  const logIt = () => {
    const raw = draft.trim();
    if (raw === '') { void write(null); return; }
    const n = Number(raw);
    // ⚠ Rubbish is REFUSED, not treated as a clear. Clearing has its own gesture
    // (an empty box, or the ↺ link) and silently discarding an override because
    // the trader fat-fingered a character is the same class of loss D61 is about.
    if (!Number.isFinite(n)) { setError('That is not a number — clear the box to go back to your imported trades.'); return; }
    void write(n);
  };

  // ⚠ The verdict is reached on the OVERRIDE when one is set, and on the imported
  // figure otherwise — never on the draft in the box, which has not been logged.
  const pnl = override ? (contract.manualPnl as number) : (livePnlUsd ?? 0);
  const v = checkInVerdict(contract, pnl, override);
  const dll = contract.personalDLL;

  return (
    <>
      <p className="muted jp-ci-source">
        {override ? (
          <>
            Showing your <b>self-reported</b> P&amp;L.{' '}
            <button
              type="button" className="linkish" disabled={busy}
              onClick={() => void write(null)}
            >↺ use live (imported trades)</button>
          </>
        ) : (
          <>Auto-computed from your imported trades. Override here if you haven’t imported yet.</>
        )}
      </p>

      <div className="row gap-8 jp-ci-entry">
        <label className="jp-ci-label" htmlFor={`jp-ci-${date}`}>Current P&amp;L $</label>
        <input
          id={`jp-ci-${date}`} type="number" step="1" inputMode="decimal"
          value={draft} disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') logIt(); }}
        />
        <button type="button" className="btn primary" disabled={busy} onClick={logIt}>
          {busy ? 'Saving…' : 'Log it'}
        </button>
        <span className="muted mono jp-ci-src">{override ? 'self-reported' : 'live'}</span>
      </div>

      {error ? <p className="red" role="alert">{error}</p> : null}

      <div className={`jp-ci-verdict ${v.tone}`} role="status">
        {v.kind === 'stopped' ? (
          <><b>STOP — daily loss limit hit.</b> You pledged to stop at {money(-dll)}.</>
        ) : v.kind === 'warning' ? (
          <><b>Warning — {v.dllPct}% of your daily loss limit used.</b> {money(v.room)} to your stop.</>
        ) : v.kind === 'target' ? (
          <><b>Target reached.</b> {money(v.pnl)} against the {money(contract.targetProfit)} goal — protect it.</>
        ) : (
          <>
            <b>On contract.</b>{' '}
            {dll ? <>{money(v.room)} of room before your {money(-dll)} stop.</> : <>No daily loss limit set.</>}
          </>
        )}
      </div>
    </>
  );
}
